import { FC } from "react";
import { Alert, Box, Grid2 as Grid } from "@mui/material";
import { useFieldArray, UseFormReturn } from "react-hook-form";
import TextIconButton from "@components/TextIconButton";
import AddIcon from "@mui/icons-material/Add";
import { useTranslation } from "react-i18next";
import OnboardingFormSection from "@components/Onboarding/OnboardingForm/OnboardingFormSection";
import ImportantDateForm from "@components/ImportantDateForm";
import { ImportantDateCategory } from "@models/patient-important-date";
import { PatientFormType } from "@utils/patientFormHelper";

const ImportantDatesForm: FC<{
  form: UseFormReturn<PatientFormType>;
}> = ({ form }) => {
  const { t } = useTranslation();
  const { control } = form;

  const {
    fields: importantDateFields,
    append: appendImportantDate,
    remove: removeImportantDate,
    update: updateImportantDate,
  } = useFieldArray<PatientFormType, "importantDates">({
    control,
    name: "importantDates",
    keyName: "_id" as any,
  });

  const addImportantDate = (): void => {
    appendImportantDate({
      id: undefined,
      title: undefined,
      date: undefined,
      category: ImportantDateCategory.Other,
    });
  };

  const deleteImportantDate = (index: number): void => {
    const importantDate = importantDateFields[index];
    if (!importantDate) {
      return;
    }
    if (!importantDate.id) {
      removeImportantDate(index);
    } else {
      updateImportantDate(index, { ...importantDate, delete: true });
    }
  };

  return (
    <OnboardingFormSection title={t("personForm.importantDates")}>
      <Alert variant="standard" severity="info" color="info">
        {t("personForm.importantDatesDescription")}
      </Alert>
      <Grid container spacing={2} marginTop="16px">
        <Grid size={12}>
          <Grid container spacing={{ xs: 5, md: 2 }}>
            {importantDateFields.map((field, index) => {
              if (field.delete) {
                return "";
              }
              return (
                <Grid size={12} key={field._id}>
                  <ImportantDateForm
                    index={index}
                    form={form}
                    onDeleteRow={deleteImportantDate}
                  />
                </Grid>
              );
            })}
          </Grid>
        </Grid>

        <Grid size={12}>
          <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
            <TextIconButton
              iconLeft={true}
              variant="outlined"
              text={t("personForm.addImportantDateButton")}
              icon={AddIcon}
              color="primary"
              onClick={addImportantDate}
            />
          </Box>
        </Grid>
      </Grid>
    </OnboardingFormSection>
  );
};

export default ImportantDatesForm;
